import {
  EntityTypeEnum,
  PublicReturnFeeEnum,
  PublicReturnGeneralClassificationEnum,
  StreamPeriodicityEnum,
} from '@aksia/enums';
import { StoreModelDate, UTILS } from '@aksia/infrastructure';
import {
  AUMStreamPoint,
  DOMAINS,
  IInvestmentVehicle,
  IPeriodicStream,
  IPeriodicStreamPoint,
  PERIODIC_STREAM_GROUPS,
  PERIODIC_STREAM_REQUEST_MODE,
  PERIODIC_STREAM_URIS,
  PERIODIC_STREAMS,
  PeriodicStream,
  PeriodicStreamPoint,
  PublicReturnStreamPoint,
  TPeriodicStream,
  TPeriodicStreamRequest,
  TPeriodicStreamUpdateRequest,
} from '@aksia/models';
import {
  toStoreModel,
  toStoreModelCollection,
  toStoreModelDTO,
} from '../../infrastructure/model/store-model.mapper';

//#region Types

export type TGridStreamPoint =
  PeriodicStreamPoint | AUMStreamPoint | PublicReturnStreamPoint;

export type TGridStream = PeriodicStream<TGridStreamPoint>;

export type TStreamStartDates = {
  companyAUM?: Date;
  programAUM?: Date;
  stream?: Date;
};

export type TStreamPointField =
  | 'value'
  | 'source'
  | 'feeType'
  | 'classification'
  | 'estimationAsOf'
  | 'currency';

export type TPeriodicsField = Exclude<TStreamPointField, 'value'>;

export type TStreamPointError = {
  field: TStreamPointField;
  label: string;
};

export type TStreamPointSnapshot = {
  value: unknown;
  source: unknown;
  feeType: unknown;
  classification: unknown;
  estimationAsOf: number | undefined;
  currency: unknown;
};

export type TStreamPointChange = {
  values: Array<unknown>;
  dataItem: Array<TGridStreamPoint>;
};

export type TStreamSelectionCell = {
  selected?: boolean;
  dataItem?: TGridStreamPoint;
};

//#endregion

//#region Requests

export const periodicStreamId = (
  stream: Pick<PeriodicStream, 'streamType' | 'entityId' | 'entityTypeId'>,
) => `${stream.streamType}_${stream.entityId}_${stream.entityTypeId}`;

export const isAUMStream = (streamType: TPeriodicStream | undefined) =>
  !!streamType && PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType);

export const isPublicReturnsStream = (
  streamType: TPeriodicStream | undefined,
) => streamType === PERIODIC_STREAMS.PUBLIC_RETURNS;

export function streamTypesFor(
  entityTypeId: EntityTypeEnum,
  isOpenEnd?: boolean,
): Array<TPeriodicStream> {
  switch (entityTypeId) {
    case EntityTypeEnum.Fund:
      return isOpenEnd
        ? [PERIODIC_STREAMS.PUBLIC_RETURNS, PERIODIC_STREAMS.FUND_AUM]
        : [];
    case EntityTypeEnum.Program:
      return [PERIODIC_STREAMS.PROGRAM_AUM];
    case EntityTypeEnum.ManagementCompany:
      return [PERIODIC_STREAMS.COMPANY_AUM];
    default:
      return [];
  }
}

export function aumStreamTypeFor(
  entityTypeId: EntityTypeEnum,
): TPeriodicStream | undefined {
  switch (entityTypeId) {
    case EntityTypeEnum.Fund:
      return PERIODIC_STREAMS.FUND_AUM;
    case EntityTypeEnum.Program:
      return PERIODIC_STREAMS.PROGRAM_AUM;
    case EntityTypeEnum.ManagementCompany:
      return PERIODIC_STREAMS.COMPANY_AUM;
    default:
      return undefined;
  }
}

export function periodicsDisplayName(streamType: TPeriodicStream): string {
  if (isPublicReturnsStream(streamType)) return 'Returns';
  if (isAUMStream(streamType)) return 'AUM';
  return PeriodicStream.getStreamPointHeader(streamType);
}

export function defaultPeriodicsFields(
  streamType: TPeriodicStream | undefined,
): Array<TPeriodicsField> {
  if (isPublicReturnsStream(streamType)) {
    return ['source', 'feeType', 'classification', 'estimationAsOf'];
  }
  if (isAUMStream(streamType)) {
    return ['source', 'currency'];
  }
  return ['source'];
}

export function historicalStreamRequest(
  entityId: number,
  entityTypeId: EntityTypeEnum,
  streamType: TPeriodicStream,
): TPeriodicStreamRequest {
  const periodicity = StreamPeriodicityEnum.Monthly.toString();
  return {
    entityId,
    entityTypeId,
    uri: PERIODIC_STREAM_URIS.URI(
      entityId,
      entityTypeId,
      streamType,
      PERIODIC_STREAM_REQUEST_MODE.HISTORICAL,
      periodicity,
    ),
    streamType,
    requestMode: PERIODIC_STREAM_REQUEST_MODE.HISTORICAL,
    periodicity,
    asOf: undefined,
  };
}

//#endregion

//#region Start Dates

export function streamStartDateFor(
  streamType: TPeriodicStream,
  dates: TStreamStartDates,
): Date | undefined {
  switch (streamType) {
    case PERIODIC_STREAMS.COMPANY_AUM:
      return dates.companyAUM;
    case PERIODIC_STREAMS.PROGRAM_AUM:
      return dates.programAUM;
    default:
      return dates.stream;
  }
}

export function programAUMStartDate(
  funds?: Array<
    Pick<IInvestmentVehicle, 'commencementOfOperations' | 'dateOfFormation'>
  >,
): Date | undefined {
  let aumStartDate: Date | undefined = undefined;
  funds?.forEach((fund) => {
    const fundStartDate = UTILS.DATE.toDate(
      fund.commencementOfOperations ?? fund.dateOfFormation,
    );
    if (
      fundStartDate &&
      (!aumStartDate || fundStartDate.getTime() < aumStartDate.getTime())
    ) {
      aumStartDate = fundStartDate;
    }
  });
  return aumStartDate;
}

//#endregion

//#region Stream Models

const asOfTime = (asOf: Date | string | undefined) =>
  (typeof asOf === 'string' ? new Date(asOf) : asOf)?.getTime() ?? 0;

const asOfYear = (asOf: Date | string | undefined) =>
  (typeof asOf === 'string' ? new Date(asOf) : asOf)!.getFullYear();

export function toPeriodicStreamModel(dto: IPeriodicStream): TGridStream {
  const streamDTO = {
    ...dto,
    datapoints: dto.datapoints?.filter((pointDTO) => pointDTO.asOf),
  };
  const streamModel = toStoreModel<IPeriodicStream, TGridStream>(
    streamDTO,
    PeriodicStream.getStreamClass(dto.streamType!) as new (
      ...args: any[]
    ) => TGridStream,
  );

  if (streamDTO.datapoints) {
    streamModel.datapoints = toStoreModelCollection(
      streamDTO.datapoints,
      PeriodicStream.getStreamPointClass(dto.streamType!) as new (
        ...args: any[]
      ) => TGridStreamPoint,
    );
  }

  if (isPublicReturnsStream(dto.streamType)) {
    streamModel.datapoints?.forEach((point) => {
      const returnPoint = point as PublicReturnStreamPoint;
      returnPoint.estimationAsOf = toStoreModelDate(returnPoint.estimationAsOf);
    });
    streamModel.ytd = streamModel.ytd?.map((point) =>
      toStoreModel(
        { ...point, asOf: UTILS.DATE.toLocalDate(point.asOf) },
        PublicReturnStreamPoint,
      ),
    );
  }

  return streamModel;
}

const toStoreModelDate = (value: Date | string | undefined) => {
  const localDate = UTILS.DATE.toLocalDate(value);
  return localDate ? new StoreModelDate(localDate) : undefined;
};

export function fillPeriodicStream<T extends TGridStream>(
  streamModel: T,
  streamStartDate: Date | undefined,
  today: Date = new Date(),
): T {
  const currentYear = today.getFullYear();
  const minStreamYear = Math.min(
    ...(streamModel.datapoints
      ?.map((dp) => asOfYear(dp.asOf))
      .filter(Boolean) ?? []),
    currentYear,
  );

  const minYear = streamStartDate?.getFullYear() ?? minStreamYear;
  streamModel.minAsOf = streamStartDate ?? new Date(minYear, 0, 1);
  streamModel.maxAsOf = isPublicReturnsStream(streamModel.streamType)
    ? UTILS.DATE.toEndOfMonth(
        new Date(today.getFullYear(), today.getMonth() + 1, -1),
      )
    : today;
  if (!streamModel.datapoints) {
    streamModel.datapoints = [];
  }

  const pointClass = PeriodicStream.getStreamPointClass(
    streamModel.streamType!,
  ) as new (...args: any[]) => TGridStreamPoint;

  Array.from({ length: currentYear - minYear + 1 }).forEach((_, i) => {
    const year = minYear + i;

    Array.from({ length: 12 }).forEach((_, month) => {
      const asOf = UTILS.DATE.toEndOfMonth(new Date(year, month, 1))!;
      const exists = streamModel.datapoints?.some((point) =>
        UTILS.DATE.isSameDate(point.asOf!, asOf),
      );
      if (exists) return;

      streamModel.datapoints?.splice(
        i * 12 + month,
        0,
        toStoreModel({ asOf }, pointClass),
      );
    });

    if (isPublicReturnsStream(streamModel.streamType)) {
      if (!streamModel.ytd) {
        streamModel.ytd = [];
      }
      const exists = streamModel.ytd.some(
        (point) => point?.asOf?.getFullYear() === year,
      );
      if (!exists) {
        streamModel.ytd.splice(
          i,
          0,
          toStoreModel(
            { asOf: UTILS.DATE.toEndOfMonth(new Date(year, 11, 1)) },
            pointClass,
          ),
        );
      }
    }
  });

  streamModel.datapoints.forEach((point) => {
    const pointAsOf = asOfTime(point.asOf);
    if (
      pointAsOf <= streamModel.minAsOf!.getTime() ||
      pointAsOf > streamModel.maxAsOf!.getTime()
    ) {
      point.$disabled = true;
    }
  });

  const yearGroups = Array.from(
    new Map(
      Object.entries(
        Object.groupBy(
          streamModel.datapoints.toSorted((a, b) =>
            asOfTime(a.asOf) < asOfTime(b.asOf) ? -1 : 1,
          ),
          (point) => asOfYear(point.asOf),
        ),
      ),
    ),
  ).sort((a, b) => (+a[0] < +b[0] ? 1 : -1));

  streamModel.datapoints = yearGroups.flatMap((group) => group[1]!);
  streamModel.ytd = streamModel.ytd
    ?.toSorted((a, b) =>
      a.asOf!.getFullYear() < b.asOf!.getFullYear() ? -1 : 1,
    )
    .toReversed();

  return streamModel;
}

export function initStreamSelection<T extends TGridStream>(stream: T): T {
  stream.historyIsLoaded = true;
  stream.selectedDataPoint = stream.datapoints?.at(0);
  stream.selectedAsOf = stream.selectedDataPoint?.asOf;
  stream.selectedSource = stream.selectedDataPoint?.source;

  if (isPublicReturnsStream(stream.streamType)) {
    const point = stream.selectedDataPoint as PublicReturnStreamPoint;
    stream.selectedFeeType = point?.feeType;
    stream.selectedClassification = point?.classification;
    stream.selectedEstimationAsOf = point?.estimationAsOf;
  }

  if (isAUMStream(stream.streamType)) {
    stream.selectedCurrency = (
      stream.datapoints?.find(
        (point) => (point as AUMStreamPoint).currency,
      ) as AUMStreamPoint
    )?.currency;
  }

  return stream;
}

export function toYtdModels(
  ytd: Array<IPeriodicStreamPoint>,
): Array<PublicReturnStreamPoint> {
  return toStoreModelCollection(
    ytd.map((point) => ({
      ...point,
      value: point.value === 0 ? undefined : point.value,
    })),
    PublicReturnStreamPoint,
  );
}

//#endregion

//#region Validation

export function streamPointErrors(
  streamType: TPeriodicStream | undefined,
  point: TGridStreamPoint | undefined,
): Array<TStreamPointError> {
  if (!point || UTILS.OBJECT.isNil(point.value)) return [];

  const checks: Array<TStreamPointError & { isInvalid: boolean }> =
    isPublicReturnsStream(streamType)
      ? [
          { field: 'source', label: 'Source', isInvalid: !point.source },
          {
            field: 'feeType',
            label: 'Fee Type',
            isInvalid: !(point as PublicReturnStreamPoint).feeType,
          },
          {
            field: 'classification',
            label: 'Classification',
            isInvalid: !(point as PublicReturnStreamPoint).classification,
          },
        ]
      : isAUMStream(streamType)
        ? [
            { field: 'source', label: 'Source', isInvalid: !point.source },
            {
              field: 'currency',
              label: 'Currency',
              isInvalid: !(point as AUMStreamPoint).currency,
            },
          ]
        : [];

  return checks
    .filter((check) => check.isInvalid)
    .map(({ field, label }) => ({ field, label }));
}

export function streamPointErrorMessage(
  streamType: TPeriodicStream | undefined,
  point: TGridStreamPoint | undefined,
  errors: Array<TStreamPointError> = streamPointErrors(streamType, point),
): string | undefined {
  if (errors.length === 0) return undefined;
  const fields = errors.map((error) => error.label).join(', ');
  return isPublicReturnsStream(streamType)
    ? `${fields} should not be empty for ${UTILS.FORMAT.toDateFormat(point?.asOf, 'MMM-yyyy')}.`
    : `${fields} should not be empty.`;
}

//#endregion

//#region Edits

export const isSavableStreamPoint = (point: {
  value?: unknown;
  $isDeleted?: boolean;
}) => Number.isFinite(point.value) || !!point.$isDeleted;

export function applyStreamPointValueChange(
  stream: TGridStream,
  points: Array<TGridStreamPoint>,
  wasValued: (point: TGridStreamPoint) => boolean,
  forceDelete: boolean = false,
): void {
  const deleteField = isAUMStream(stream.streamType)
    ? 'isDeleted'
    : 'markedForDeletion';

  points.forEach((point) => {
    const deletable = point as unknown as Record<string, unknown>;
    if (isAUMStream(stream.streamType)) {
      (point as AUMStreamPoint).currency =
        (point as AUMStreamPoint).currency || stream.selectedCurrency;
    }

    if (point.value === undefined && (wasValued(point) || forceDelete)) {
      deletable[deleteField] = true;
    } else {
      point.$isDeleted = false;
      deletable[deleteField] = false;
    }
  });
}

export function applyFieldToPoints(
  points: Array<TGridStreamPoint | undefined> | undefined,
  field: TPeriodicsField,
  value: unknown,
): Array<TGridStreamPoint> {
  const changed: Array<TGridStreamPoint> = [];
  points
    ?.filter(
      (point): point is TGridStreamPoint =>
        !!point && !UTILS.OBJECT.isNil(point.value),
    )
    .forEach((point) => {
      (point as unknown as Record<string, unknown>)[field] = value;
      changed.push(point);
    });
  return changed;
}

export function markedSelectionChanges(
  cells: Array<TStreamSelectionCell> | undefined,
): Partial<TGridStream> {
  const selectedDataPoint = cells?.find((cell) => cell.selected)?.dataItem;
  const single =
    cells?.length === 1
      ? (cells[0].dataItem as PublicReturnStreamPoint | undefined)
      : undefined;

  return {
    selectedDataPoint,
    markedDataPoints: cells?.map(
      (cell) => cell.dataItem,
    ) as Array<TGridStreamPoint>,
    selectedAsOf: selectedDataPoint?.asOf,
    selectedSource: single?.source,
    selectedFeeType: single?.feeType as PublicReturnFeeEnum | undefined,
    selectedClassification: single?.classification as
      PublicReturnGeneralClassificationEnum | undefined,
    selectedEstimationAsOf: single?.estimationAsOf,
  };
}

//#endregion

//#region Dirty Tracking

export const pointSnapshot = (
  point: TGridStreamPoint,
): TStreamPointSnapshot => ({
  value: point.value,
  source: point.source,
  feeType: (point as PublicReturnStreamPoint).feeType,
  classification: (point as PublicReturnStreamPoint).classification,
  estimationAsOf: UTILS.DATE.toLocalDate(
    (point as PublicReturnStreamPoint).estimationAsOf,
  )?.getTime(),
  currency: (point as AUMStreamPoint).currency,
});

export function isPointDirty(
  point: TGridStreamPoint,
  snapshot: TStreamPointSnapshot | undefined,
): boolean {
  if (!snapshot) return false;
  const current = pointSnapshot(point);
  return (Object.keys(current) as Array<keyof TStreamPointSnapshot>).some(
    (key) => current[key] !== snapshot[key],
  );
}

//#endregion

//#region Save

export function buildStreamUpdateRequest(
  stream: TGridStream,
  points: Array<TGridStreamPoint>,
  investmentProgramId?: number,
): TPeriodicStreamUpdateRequest {
  const streamDomain = Object.entries(PERIODIC_STREAMS).find(
    ([, value]) => value === stream.streamType,
  )?.[0] as keyof typeof DOMAINS.PERIODIC_STREAMS;
  const domain = DOMAINS.PERIODIC_STREAMS[streamDomain];
  const uriEntityId = isPublicReturnsStream(stream.streamType)
    ? (investmentProgramId?.toString() ?? '')
    : stream.entityId!.toString();

  return {
    entityId: stream.entityId!,
    entityTypeId: stream.entityTypeId!,
    httpMethod: domain.HTTP,
    uri: domain.URI.SET?.replaceAll('{streamType}', stream.streamType!)
      ?.replaceAll('{entityId}', uriEntityId)
      ?.replaceAll('{periodicity}', stream.periodicity?.toString() ?? ''),
    streamType: stream.streamType!,
    datapoints: toStoreModelDTO(points) as Array<IPeriodicStreamPoint>,
    periodicity: stream.periodicity?.toString(),
  };
}

//#endregion
